import { supabase } from "../../supabaseClient";
import { clearAuthStorage } from "../../utils/storageCleanup";
import {
  isAbortLikeError,
  isTransientNetworkError,
} from "../../utils/errorClassifier";

import { errorLogService } from "../errorLogService";
import { recordAuthDiagnostic } from "./authDiagnosticsService";
import logger from "../../utils/logger";
import { getCurrentUserProfile } from "./currentUserProfileService";
import { withTimeout, TimeoutError } from "../../utils/withTimeout";

// Сетевой ОБРЫВ соединения (не ответ сервера): ретраим.
// Не ретраим 429 (rate-limit) и hook-ошибки (письмо могло уйти).
const isConnectionDrop = (error) => {
  if (!error) return false;
  const message = error?.message?.toLowerCase?.() || "";
  const name = error?.name?.toLowerCase?.() || "";
  const status = Number(error?.status);
  if (status === 429) return false; // rate-limit — не ретраим
  if (status >= 400 && status < 600) return false; // сервер ответил — не сетевой обрыв
  return (
    isAbortLikeError(error) ||
    name.includes("networkerror") ||
    message.includes("failed to fetch") ||
    message.includes("network") ||
    message.includes("timed out") ||
    message.includes("timeout") ||
    message.includes("socket") ||
    message.includes("econnreset") ||
    message.includes("econnrefused") ||
    message.includes("err_timed_out")
  );
};

const isSendEmailHookFailure = (error) => {
  const message = error?.message?.toLowerCase?.() || "";
  const code = error?.code?.toLowerCase?.() || "";
  const status = Number(error?.status);

  return (
    message.includes("error running hook uri") ||
    message.includes("send email hook") ||
    message.includes("hook uri") ||
    message.includes("unexpected_failure") ||
    code === "unexpected_failure" ||
    (status === 500 && message.includes("hook"))
  );
};

/**
 * Отправить код авторизации (OTP) на email
 */
export const sendAuthCode = async (email) => {
  const attemptId = (globalThis.crypto && globalThis.crypto.randomUUID && globalThis.crypto.randomUUID()) || (Date.now().toString(36) + Math.random().toString(36).slice(2));
  const t0 = Date.now();
  try {
    const trimmedEmail = email.trim().toLowerCase();
    recordAuthDiagnostic({ attemptId, email: trimmedEmail, stage: 'ui_email_submitted', status: 'accepted' });
    // Вспомогательная функция для задержки
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // 1. Проверяем наличие почты через безопасный RPC
    // Пробуем до 3 раз только при временных ошибках
    let userExists = false;
    let checkError = null;

    for (let attempt = 1; attempt <= 3; attempt++) {
      // Обёрнуто в withTimeout: при зависшей сети (например, VPN) RPC может
      // не вернуть ответ — иначе кнопка «Отправка...» висела бы вечно.
      const result = await withTimeout(
        supabase.rpc("auth_email_exists", {
          p_email: trimmedEmail,
        }),
        8000,
        "Не удалось получить ответ от сервера",
      ).catch((e) => {
        checkError = e;
        return { data: null, error: e };
      });

      userExists = Boolean(result.data);
      checkError = result.error;
      recordAuthDiagnostic({
        attemptId,
        email: trimmedEmail,
        stage: 'email_lookup',
        status: checkError ? 'failed' : (userExists ? 'success' : 'not_found'),
        errorCode: checkError?.code || checkError?.name || null,
        httpStatus: checkError?.status || null,
        latencyMs: Date.now() - t0,
      });

      if (!checkError || attempt === 3) break;

      // Таймаут сети — не ретраим, это не временный сбой, а проблема соединения
      if (checkError instanceof TimeoutError) break;

      logger.debug(`Auth check attempt ${attempt} failed, retrying...`);
      await sleep(1000); // Ждем 1 секунду перед повтором
    }

    if (checkError) {
      logger.error("Error checking user existence:", checkError);
      // Серверный след: проверка почты не прошла (сеть/таймаут). Видно, долетает ли
      // самый первый запрос логина от устройства пользователя.
      void errorLogService.logError({
        message: "email_lookup_failed",
        severity: "warn",
        context: {
          app_context: {
            module: "auth",
            email_domain: trimmedEmail?.split("@")[1] || null,
            error_name: checkError?.name || null,
            error_message: String(checkError?.message || "").slice(0, 200),
            latency_ms: Date.now() - t0,
          },
        },
      });

      // Если проверка упала из-за временного сетевого сбоя, не блокируем вход на этом шаге.
      // Иначе пользователь с корректной почтой получает ложное "не найден".
      if (!isTransientNetworkError(checkError)) {
        return {
          success: false,
          error:
            "Не удалось проверить почту в базе. Попробуйте еще раз через несколько секунд.",
        };
      }
    }

    if (!userExists && !checkError) {
      return {
        success: false,
        error:
          "Такая почта в базе данных не найдена. Если вы уверены, что почта введена правильно, напишите разработчику.",
      };
    }

    // 2. Если пользователь найден, отправляем OTP.
    // Обёрнуто в withTimeout: GoTrue может отправить письмо (через hook) но не
    // вернуть HTTP-ответ клиенту вовремя → promise висел вечно, кнопка «отправка».
    // Ретраим только при чисто СЕТЕВОМ обрыве (connection drop) — когда запрос
    // физически не дошёл/не вернулся. При 429 (rate-limit) и hook-ошибках НЕ ретраим.
    const OTP_RETRIES = 3;
    const OTP_RETRY_DELAYS = [2000, 4000]; // сб, после 1-й и 2-й попытки
    let lastOtpError = null;
    let otpResult = null;

    for (let attempt = 1; attempt <= OTP_RETRIES; attempt++) {
      try {
        const { data, error } = await withTimeout(
          supabase.auth.signInWithOtp({
            email: trimmedEmail,
            options: {
              shouldCreateUser: false,
              // Возвращать magic-ссылку на тот же хост, где начали вход
              // (mgfood.ru / mg.mkucher.ru / dev.mgfood.ru).
              emailRedirectTo: window.location.origin,
            },
          }),
          10000,
          "Не удалось получить ответ от сервера",
        );

        if (error) throw error;
        otpResult = { success: true, data };
        // Сервер принял OTP-запрос (подробности — в auth_login_log на сервере).
        // В app_errors не пишем: спам info топил реальные ошибки.
        logger.debug("otp_request_sent_to_server", {
          email_domain: trimmedEmail?.split("@")[1] || null,
          attempt: attempt,
          latency_ms: Date.now() - t0,
        });
        recordAuthDiagnostic({
          attemptId,
          email: trimmedEmail,
          stage: 'otp_request',
          status: 'accepted',
          latencyMs: Date.now() - t0,
        });
        break; // успех
      } catch (e) {
        lastOtpError = e;
        // Логируем неудачу OTP-запроса (сеть/таймаут/ошибка сервера) — видно, долетает ли попытка.
        void errorLogService.logError({
          message: "otp_request_failed",
          severity: "warn",
          context: {
            app_context: {
              module: "auth",
              email_domain: trimmedEmail?.split("@")[1] || null,
              attempt: attempt,
              error_name: e?.name || null,
              error_message: String(e?.message || "").slice(0, 200),
              latency_ms: Date.now() - t0,
            },
          },
        });
        // Ретраим только сетевой обрыв, и только если ещё есть попытки
        if (!isConnectionDrop(e) || attempt === OTP_RETRIES) break;
        const delay = OTP_RETRY_DELAYS[attempt - 1] || 4000;
        logger.warn("Auth OTP connection drop, retry", {
          attempt,
          next_delay_ms: delay,
          error_name: e?.name,
        });
        await sleep(delay);
      }
    }

    if (otpResult) return otpResult;
    if (lastOtpError) throw lastOtpError;
    throw new Error("Не удалось отправить код");
  } catch (error) {
    if (error instanceof TimeoutError || isSendEmailHookFailure(error)) {
      const context = {
        error_name: error?.name,
        error_code: error?.code,
        error_status: error?.status,
        email_domain: email?.split("@")[1],
        flow_type: "send_otp",
        delivery_ambiguous: true,
      };

      logger.warn("Auth Code hook delivery ambiguous:", error, context);
      void errorLogService.logError({
        message: "Auth Code hook delivery ambiguous",
        error,
        context,
        severity: "warn",
      });

      return {
        success: true,
        data: null,
        deliveryAmbiguous: true,
        warning:
          "Если отправка пин-кода долго не производится, отключите VPN, повторите попытку или перезагрузите приложение. Если код придет, введите его ниже.",
      };
    }

    const hasIndexedDB = typeof window !== "undefined" && "indexedDB" in window;
    recordAuthDiagnostic({
      attemptId,
      email: (email || "").trim().toLowerCase(),
      stage: 'otp_request',
      status: 'failed',
      errorCode: error?.code || error?.name || null,
      httpStatus: error?.status || null,
      latencyMs: Date.now() - t0,
    });
    logger.error("Auth Code error:", error, {
      error_name: error?.name,
      error_code: error?.code,
      error_status: error?.status,
      has_indexedDB: hasIndexedDB,
      user_agent: navigator.userAgent,
      is_secure_context: window.isSecureContext,
      email_domain: email?.split("@")[1],
      flow_type: "send_otp",
    });
    return {
      success: false,
      error: error.message || "Не удалось отправить код",
    };
  }
};

const resolveRoleFromProfile = (profileResult, sessionUser, fallbackRole = "student") => {
  if (profileResult?.success && profileResult.profile?.role) {
    return profileResult.profile.role;
  }

  if (profileResult?.status === "missing_profile" || profileResult?.status === "inactive_profile") {
    return fallbackRole;
  }

  return (
    sessionUser?.app_metadata?.role ||
    sessionUser?.user_metadata?.role ||
    fallbackRole
  );
};

/**
 * Alias для обратной совместимости
 */
export const sendMagicLink = sendAuthCode;

/**
 * Проверка OTP кода
 * @param {string} email
 * @param {string} token - 6-значный код
 * @param {boolean} rememberMe - Запомнить ли сессию на 2 месяца
 */
export const verifyOtpCode = async (email, token, rememberMe = true) => {
  const attemptId = (globalThis.crypto && globalThis.crypto.randomUUID && globalThis.crypto.randomUUID()) || (Date.now().toString(36) + Math.random().toString(36).slice(2));
  const t0 = Date.now();
  recordAuthDiagnostic({ attemptId, email, stage: 'otp_verify', status: 'started' });
  logger.auth("[verifyOtpCode] Starting verification for:", email);
  if (!email || !token)
    return { success: false, error: "Email и код подтверждения обязательны" };
  try {
    logger.auth(
      "[verifyOtpCode] Calling supabase.auth.verifyOtp with token length:",
      token.length,
    );
    // Адаптивный таймаут: на мобильных сетях/VPN запрос может ехать минуты
    // (HTTP/2 upload stall) — 10с рвал соединение и показывал «нет ответа от сервера»,
    // хотя запрос потом доходил и PIN валидировался сервером.
    const isSlowVerifyNetwork = (() => {
      try {
        if (typeof navigator !== "undefined" && navigator.connection) {
          const et = (navigator.connection.effectiveType || "").toLowerCase();
          if (et === "slow-2g" || et === "2g") return true;
          if (et === "3g" && (navigator.connection.rtt || 0) >= 1500) return true;
        }
      } catch (e) {
        // navigator.connection недоступен — обычная сеть
      }
      return false;
    })();
    const verifyOnce = () =>
      withTimeout(
        supabase.auth.verifyOtp({
          email,
          token,
          // GoTrue creates recovery_token for this OTP flow.
          type: "recovery",
        }),
        isSlowVerifyNetwork ? 40000 : 15000,
        "Не удалось получить ответ от сервера",
      );

    // Автоповтор: на медленной сети первый запрос может не уложиться в таймаут,
    // хотя сервер его потом валидировал. Один тихий ретрай — как в банковских приложениях.
    let data, error;
    try {
      const first = await verifyOnce();
      data = first.data;
      error = first.error;
    } catch (firstErr) {
      if (firstErr instanceof TimeoutError || firstErr?.name === "TimeoutError") {
        logger.warn("[verifyOtpCode] timeout, retrying once", {
          slow_network: isSlowVerifyNetwork,
        });
        const second = await verifyOnce();
        data = second.data;
        error = second.error;
      } else {
        throw firstErr;
      }
    }

    logger.auth("[verifyOtpCode] Supabase result:", { data, error });

    if (error) throw error;
    if (!data.session) throw new Error("Не удалось создать сессию");
    recordAuthDiagnostic({
      attemptId,
      email,
      stage: 'otp_verify',
      status: 'success',
      latencyMs: Date.now() - t0,
    });

    // Fire-and-forget: логируем устройство/браузер успешного входа для панели
    // диагностики мегаадмина. IP пишется server-side триггером (audit → auth_login_log);
    // здесь дополняем user_agent/device. НИКОГДА не блокирует и не ломает вход.
    try {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const platform =
        typeof navigator !== "undefined" ? navigator.platform || "" : "";
      void supabase
        .from("auth_login_log")
        .insert({
          email: (email || "").toLowerCase(),
          event: "login_success",
          success: true,
          user_agent: ua,
          device_info: platform,
          meta: { source: "client" },
        })
        .then(() => {})
        .catch(() => {});
    } catch (_e) {
      /* logging must never break login */
    }

    if (rememberMe) {
      try {
        localStorage.setItem(
          "mgfood_session_info",
          JSON.stringify({
            remember_me: true,
            created_at: Date.now(),
            expires_at: Date.now() + 60 * 24 * 60 * 60 * 1000,
          }),
        );
      } catch (e) {
        logger.error("Failed to save session info:", e);
      }
    }

    const profileResult = await getCurrentUserProfile();
    const finalRole = resolveRoleFromProfile(
      profileResult,
      data.session.user,
      "student",
    );

    return {
      success: true,
      user: data.user,
      session: data.session,
      role: finalRole,
      profile: profileResult.profile || null,
    };
  } catch (error) {
    const isIdbError =
      error?.message?.includes("IndexedDB") ||
      error?.name === "QuotaExceededError" ||
      error?.name === "UnknownError";
    const hasIndexedDB = typeof window !== "undefined" && "indexedDB" in window;
    const isExpiredOtp =
      error?.code === "otp_expired" ||
      error?.message?.toLowerCase?.().includes("token has expired") ||
      error?.message?.toLowerCase?.().includes("invalid token");

    recordAuthDiagnostic({
      attemptId,
      email,
      stage: 'otp_verify',
      status: isExpiredOtp ? 'expired' : (isIdbError ? 'storage_error' : 'failed'),
      errorCode: error?.code || error?.name || null,
      httpStatus: error?.status || null,
      latencyMs: Date.now() - t0,
    });
    const logMethod = isExpiredOtp ? logger.warn.bind(logger) : logger.error.bind(logger);

    logMethod("Verify OTP error:", error, {
      error_name: error?.name,
      error_code: error?.code,
      error_status: error?.status,
      is_idb_related: isIdbError,
      has_indexedDB: hasIndexedDB,
      user_agent: navigator.userAgent,
      is_secure_context: window.isSecureContext,
      token_length: token?.length,
      email_domain: email?.split("@")[1],
      flow_type: "email_otp",
    });
    return {
      success: false,
      error: error.message || "Неверный код или истек срок действия",
    };
  }
};

/**
 * Обработка callback после клика на Magic Link
 */
export const handleAuthCallback = async () => {
  // Check both search (query params) and hash (fragment)
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, "?"),
  );

  const tokenHash =
    params.get("token_hash") ||
    hashParams.get("token_hash") ||
    params.get("token") ||
    hashParams.get("token");
  const code = params.get("code") || hashParams.get("code");
  // Implicit flow uses access_token
  const accessToken =
    params.get("access_token") || hashParams.get("access_token");
  const type = params.get("type") || hashParams.get("type") || "magiclink";

  // Case 1: PKCE code exchange
  if (code) {
    logger.auth("handleAuthCallback: PKCE flow detected, exchanging code...");
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    if (data?.session) {
      const profileResult = await getCurrentUserProfile();
      const role = resolveRoleFromProfile(
        profileResult,
        data.session.user,
        "student",
      );
      return {
        user: data.session.user,
        session: data.session,
        role,
        profile: profileResult.profile || null,
      };
    }
  }

  // Case 2: Implicit flow uses access_token
  if (accessToken) {
    logger.auth("handleAuthCallback: Implicit flow detected...");
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    if (data?.session) {
      const profileResult = await getCurrentUserProfile();
      const role = resolveRoleFromProfile(
        profileResult,
        data.session.user,
        "student",
      );
      return {
        user: data.session.user,
        session: data.session,
        role,
        profile: profileResult.profile || null,
      };
    }
  }

  if (!tokenHash && !accessToken && !code) {
    throw new Error("Отсутствуют параметры авторизации в URL");
  }

  // Case 3: For PKCE flow (token_hash / verifyOtp style)
  if (tokenHash) {
    logger.auth("handleAuthCallback: token_hash flow detected...");
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: type === "email" ? "magiclink" : type,
    });

    if (error) throw error;
    if (!data.session) throw new Error("Не удалось создать сессию");

    const profileResult = await getCurrentUserProfile();
    const role = resolveRoleFromProfile(profileResult, data.session.user);
    return {
      user: data.user,
      session: data.session,
      role,
      profile: profileResult.profile || null,
    };
  }

  throw new Error("Неподдерживаемый метод авторизации");
};

/**
 * Ручная проверка Magic Link (если пользователь вставил ссылку вручную)
 */
export const verifyManualLink = async (urlString) => {
  try {
    // Если это просто токен (без спец символов), можно попробовать использовать его как есть,
    // но обычно пользователи вставляют всю ссылку.
    let url;
    try {
      url = new URL(urlString);
    } catch (e) {
      // Это не валидный URL, попробуем обработать как фрагмент или токен
      if (urlString.includes("token_hash=")) {
        url = new URL(`https://fake.com/?${urlString.replace(/^#/, "")}`);
      } else {
        throw new Error(
          "Некорректный формат ссылки. Пожалуйста, скопируйте всю ссылку из письма.",
        );
      }
    }

    const params = new URLSearchParams(url.search);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, "?"));

    const tokenHash =
      params.get("token_hash") ||
      hashParams.get("token_hash") ||
      params.get("token") ||
      hashParams.get("token");
    const type = params.get("type") || hashParams.get("type") || "magiclink";

    if (!tokenHash) {
      throw new Error("В ссылке не найден токен авторизации (token_hash)");
    }

    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: type === "email" ? "magiclink" : type,
    });

    if (error) throw error;
    if (!data.session) throw new Error("Не удалось создать сессию");

    const profileResult = await getCurrentUserProfile();
    const role = resolveRoleFromProfile(profileResult, data.session.user);
    return {
      success: true,
      user: data.user,
      session: data.session,
      role,
      profile: profileResult.profile || null,
    };
  } catch (error) {
    logger.error("verifyManualLink error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Logout
 */
export const logout = async () => {
  await supabase.auth.signOut();
  clearAuthStorage();
};

/**
 * Логирование согласия пользователя
 * @param {string} email
 * @param {string} consentType - 'terms', 'personal_data', 'privacy_policy'
 */
export const logUserConsent = async (email, consentType) => {
  const trimmedEmail = email.trim().toLowerCase();
  const typeMapping = {
    terms: "terms_of_service",
    personal_data: "data_processing",
    privacy_policy: "privacy_policy",
  };

  const finalType = typeMapping[consentType] || consentType;

  try {
    await supabase.from("user_consents").insert([
      {
        email: trimmedEmail,
        consent_type: finalType,
        ip_address: null,
        user_agent: null,
      },
    ]);
  } catch (err) {
    // Тихая ошибка для согласий, чтобы не пугать пользователя в консоли
  }
};

export default supabase;
